import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {SHOW_ERROR, SHOW_SUCCESS} from '../../../util'
import {RootState} from '../../store'
import {AxiosError} from 'axios'
import {
  getAllCollections,
  createCollection,
  updateCollection,
} from '../../../api/endpoints/collection'

export interface collectionState {
  collections: any[]
  status: string
  loadingStatus: string
  error: string | null
}

export const initialState: collectionState = {
  collections: [],
  status: 'idle',
  loadingStatus: 'idle',
  error: null,
}

export const fetchCollections = createAsyncThunk('collections/fetch', async () => {
  try {
    const res: any = await getAllCollections()
    const {data, success, message} = res.data

    return {
      data,
      success,
    }
  } catch (error) {
    console.error(error)
    return error
  }
})

export const createCollectionAction = createAsyncThunk(
  'collections/create',
  async (formData: any, thunkAPI: any) => {
    try {
      const res: any = await createCollection(formData)
      const responsePayload = res?.data ?? res?.response?.data
      if (!responsePayload) {
        throw new Error('Empty response from server')
      }
      const {data, success, message} = responsePayload
      if (success) {
        SHOW_SUCCESS(true, 'Collection created successfully')
        thunkAPI.dispatch(fetchCollections())
      } else {
        SHOW_ERROR(true, message)
        return thunkAPI.rejectWithValue({ message })
      }

      return {
        data,
        success,
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ??
        error?.response?.data?.data?.error ??
        error?.message ??
        'Something went wrong'
      SHOW_ERROR(true, apiMessage)
      return thunkAPI.rejectWithValue({ message: apiMessage })
    }
  }
)

export const updateCollectionAction = createAsyncThunk(
  'collections/update',
  async (formData: any, thunkAPI: any) => {
    try {
      const res: any = await updateCollection(formData.data, formData.id)
      const responsePayload = res?.data ?? res?.response?.data
      if (!responsePayload) {
        throw new Error('Empty response from server')
      }
      const {data, success, message} = responsePayload
      if (success) {
        SHOW_SUCCESS(true, 'Collection updated successfully')
        thunkAPI.dispatch(fetchCollections())
      } else {
        SHOW_ERROR(true, message)
        return thunkAPI.rejectWithValue({ message })
      }

      return {
        formData,
        data,
        success,
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ??
        error?.response?.data?.data?.error ??
        error?.message ??
        'Something went wrong'
      SHOW_ERROR(true, apiMessage)
      return thunkAPI.rejectWithValue({ message: apiMessage })
    }
  }
)

const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCollections.pending, (state) => {
      state.status = 'loading'
      state.error = null
    })

    builder.addCase(fetchCollections.fulfilled, (state, {payload}) => {
      const {data, success}: any = payload
      if (success) {
        state.collections = data || []
        state.status = 'idle'
      }
    })

    builder.addCase(fetchCollections.rejected, (state, {payload}) => {
      state.error = payload as string
      state.status = 'idle'
    })

    builder.addCase(createCollectionAction.pending, (state) => {
      state.loadingStatus = 'loading'
    })
    builder.addCase(createCollectionAction.fulfilled, (state) => {
      state.loadingStatus = 'idle'
    })
    builder.addCase(createCollectionAction.rejected, (state, action: any) => {
      state.error = (action.payload && action.payload.message) || action.error?.message || 'Request failed'
      state.loadingStatus = 'idle'
    })

    builder.addCase(updateCollectionAction.pending, (state) => {
      state.loadingStatus = 'loading'
    })
    builder.addCase(updateCollectionAction.fulfilled, (state, action) => {
      state.loadingStatus = 'idle'
      const {formData, data, success}: any = action.payload
      if (success) {
        const index = state.collections?.findIndex((col: any) => col?.id === formData?.id)
        if (index !== -1) {
          state.collections[index] = data
        }
      }
    })
    builder.addCase(updateCollectionAction.rejected, (state, action: any) => {
      state.error = (action.payload && action.payload.message) || action.error?.message || 'Request failed'
      state.loadingStatus = 'idle'
    })
  },
})

export default collectionSlice.reducer

export const collectionSelector = (state: RootState) => state.collection.collections
export const collectionStatusSelector = (state: RootState) => state.collection.status
export const collectionLoadingStatusSelector = (state: RootState) => state.collection.loadingStatus

